/**
 * Показатели прогресса и XP.
 *
 * Четыре показателя независимы (ТЗ FR-GM-01), XP начисляется только за
 * полезные действия и с убывающей отдачей (FR-GM-04, FR-GM-05).
 */

import type {
  Baseline,
  GrowthReport,
  ProgressIndicators,
  SkillTier,
  TypingMetrics,
  XpReason,
  XpTransaction,
} from '@type2x/shared-contracts';
import { SKILL_TIERS, TARGET_ACCURACY, TARGET_WPM } from '@type2x/shared-contracts';

import { clamp01, smoothGrowth } from './normalize.js';

/** Базовая величина XP по причине начисления. */
const BASE_XP: Readonly<Record<XpReason, number>> = {
  'session-completed': 40,
  'accuracy-improved': 25,
  'new-unit-mastered': 30,
  'persistent-error-fixed': 45,
  'control-test-completed': 60,
  'scheduled-review-done': 20,
  'skill-retained': 50,
};

/** Дневной лимит начислений по одной причине (ТЗ FR-GM-05). */
const DAILY_CAP: Readonly<Record<XpReason, number>> = {
  'session-completed': 6,
  'accuracy-improved': 4,
  'new-unit-mastered': 12,
  'persistent-error-fixed': 8,
  'control-test-completed': 3,
  'scheduled-review-done': 10,
  'skill-retained': 2,
};

/** Максимальный рост скорости, учитываемый при сглаживании. */
const GROWTH_CAP = 8;

/**
 * Нижняя граница baseline, зачитываемая в рост.
 *
 * Ниже этой скорости измерение неотличимо от «не умею печатать вообще», и
 * относительный рост от неё раздувается произвольно. Ограничение снизу не даёт
 * искусственно заниженному старту дать огромное преимущество (ТЗ FR-LB-04).
 */
const MIN_CREDITED_BASELINE_WPM = 5;

/**
 * Коэффициент убывающей отдачи для n-го начисления за день.
 *
 * Повторение слишком лёгкого упражнения не должно давать бесконечный XP.
 */
export function diminishingFactor(occurrenceToday: number, reason: XpReason): number {
  const cap = DAILY_CAP[reason];
  if (occurrenceToday >= cap) return 0;
  return 1 / (1 + occurrenceToday * 0.5);
}

/**
 * Формирует XP-транзакцию.
 *
 * @param reason причина начисления;
 * @param occurrenceToday сколько раз эта причина уже сработала сегодня;
 * @param awardedAt календарная метка (ISO 8601).
 */
export function awardXp(
  reason: XpReason,
  occurrenceToday: number,
  awardedAt: string,
): XpTransaction {
  const factor = diminishingFactor(occurrenceToday, reason);
  return {
    reason,
    amount: Math.round(BASE_XP[reason] * factor),
    awardedAt,
    diminishingFactor: factor,
  };
}

/** Уровень по накопленному XP. Порог растёт квадратично. */
export function levelForXp(xp: number): number {
  if (xp <= 0) return 1;
  return Math.floor(Math.sqrt(xp / 100)) + 1;
}

/** XP, необходимый для достижения уровня. */
export function xpForLevel(level: number): number {
  if (level <= 1) return 0;
  return (level - 1) ** 2 * 100;
}

/** Достигнутый уровень владения навыком, либо null. */
export function skillTierFor(wpm: number, accuracy: number): SkillTier | null {
  let achieved: SkillTier | null = null;
  for (const tier of SKILL_TIERS) {
    if (wpm >= tier.wpm && accuracy >= tier.accuracy) achieved = tier;
  }
  return achieved;
}

/** Наглядное сравнение старта и текущего результата. */
export function buildGrowthReport(
  baseline: Baseline,
  current: TypingMetrics,
  verifiedOnUnseenText: boolean,
): GrowthReport {
  return {
    language: baseline.language,
    baselineCpm: baseline.cpm,
    currentCpm: current.cpm,
    deltaCpm: current.cpm - baseline.cpm,
    speedMultiplier: baseline.cpm > 0 ? current.cpm / baseline.cpm : 0,
    baselineAccuracy: baseline.accuracy,
    currentAccuracy: current.accuracy,
    verifiedOnUnseenText,
  };
}

/** Исходные данные для расчёта четырёх показателей. */
export interface IndicatorInput {
  readonly baseline: Baseline;
  readonly currentWpm: number;
  readonly currentAccuracy: number;
  readonly totalXp: number;
  /** Дней практики за последние две недели. */
  readonly activeDays: number;
  /** Подтверждено ли сохранение навыка отложенным тестом. */
  readonly retained: boolean;
}

/** Считает четыре независимых показателя прогресса. */
export function computeIndicators(input: IndicatorInput): ProgressIndicators {
  const skill = clamp01(
    (input.currentWpm / TARGET_WPM) * clamp01(input.currentAccuracy / TARGET_ACCURACY),
  );

  const creditedBaseline = Math.max(input.baseline.wpm, MIN_CREDITED_BASELINE_WPM);
  const growth = smoothGrowth(input.currentWpm / creditedBaseline, GROWTH_CAP);

  const regularity = clamp01(input.activeDays / 14);
  const consistency = clamp01(regularity * 0.7 + (input.retained ? 0.3 : 0));

  return { skill, growth, xp: input.totalXp, consistency };
}
